import { useEffect, useMemo, useRef } from 'react'
import videojs from 'video.js'
import Player from 'video.js/dist/types/player'
import 'video.js/dist/video-js.css'
import styles from './VideoPlayer.module.less'
import {
    BackwardButton,
    CopyVideoCurrentTime,
    ForwardButton,
    TimeWithMilliseconds,
} from './VideoPlayerCustomComponents'

export type VideoPlayerPropsType = {
    url: string
    id: string
    onError?: () => void
    onLoadedData?: () => void
}

export const VideoPlayer = (props: VideoPlayerPropsType) => {
    const videoRef = useRef<HTMLDivElement | null>(null)
    const playerRef = useRef<Player | null>(null)
    const { onError, onLoadedData } = props

    const options = useMemo(
        () => ({
            controls: true,
            experimentalSvgIcons: true,
            playbackRates: [0.15, 0.25, 0.5, 1, 1.5, 2],
            controlBar: {
                alwaysVisible: true,
                children: [
                    { name: 'customBackwardButton', align: 'left' },
                    { name: 'playToggle', align: 'left' },
                    { name: 'customForwardButton', align: 'left' },
                    { name: 'timeWithMilliseconds', align: 'left' },
                    { name: 'progressControl', align: 'left' },
                    { name: 'copyVideoCurrentTime', align: 'right' },
                    { name: 'volumePanel', align: 'right', inline: false },
                    { name: 'playbackRateMenuButton', align: 'right' },
                    { name: 'fullscreenToggle', align: 'right' },
                    { name: 'pictureInPictureToggle', align: 'right' },
                ],
            },
            sources: props.url
                ? [
                      {
                          src: props.url,
                      },
                  ]
                : [],
        }),
        [props.url]
    )

    useEffect(() => {
        if (!playerRef.current) {
            const videoElement = document.createElement('video-js')

            videoElement.classList.add('vjs-big-play-centered')
            videoRef.current!.appendChild(videoElement)

            videojs.registerComponent('timeWithMilliseconds', TimeWithMilliseconds)
            videojs.registerComponent('copyVideoCurrentTime', CopyVideoCurrentTime)
            videojs.registerComponent('customForwardButton', ForwardButton)
            videojs.registerComponent('customBackwardButton', BackwardButton)
            playerRef.current = videojs(videoElement, options, () => {
                videojs.log('player is ready')
                const player = playerRef.current!
                player.on('error', () => {
                    onError?.()
                })
                player.on('loadeddata', () => {
                    onLoadedData?.()
                })
            })
        } else {
            const player = playerRef.current
            player.src(options.sources)
        }
    }, [options, videoRef, onError, onLoadedData])

    useEffect(() => {
        const player = playerRef.current
        return () => {
            if (player && !player.isDisposed()) {
                player.dispose()
                playerRef.current = null
            }
        }
    }, [playerRef])

    return (
        <div className={styles['video-player']} id={props.id}>
            <div data-vjs-player>
                <div ref={videoRef} />
            </div>
        </div>
    )
}
